/**
 * Firestore Utilities
 * Functions for Firestore operations related to chat messages and sessions
 */

import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import app from "../../../firebase";

const db = firebase.firestore(app);

/**
 * Strip common markdown syntax for plain-text previews.
 */
const stripMarkdown = (text) => {
        if (!text) return "";
        return text
                .replace(/\*\*(.+?)\*\*/g, "$1")   // **bold**
                .replace(/\*(.+?)\*/g, "$1")         // *italic*
                .replace(/__(.+?)__/g, "$1")           // __underline__
                .replace(/_(.+?)_/g, "$1")             // _italic_
                .replace(/~~(.+?)~~/g, "$1")           // ~~strikethrough~~
                .replace(/`(.+?)`/g, "$1")             // `code`
                .replace(/^#{1,6}\s+/gm, "")          // ### headings
                .replace(/^[\-*+]\s+/gm, "")         // - bullet lists
                .replace(/^\d+\.\s+/gm, "")          // 1. numbered lists
                .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // [link](url)
                .replace(/\n{2,}/g, " ")              // collapse newlines
                .trim();
};


/**
 * Save a message to Firestore
 * @param {string} userId - User ID
 * @param {string} chatId - Chat session ID
 * @param {string} content - Message content
 * @param {string} sender - Message sender ("user" or "ai")
 * @param {Object} metadata - Optional message metadata
 * @returns {Promise<string>} Message document ID
 */
export const saveMessage = async (userId, chatId, content, sender, metadata = null) => {
        try {
                const messagePayload = {
                        content,
                        sender,
                        timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                        read: sender === "user" ? true : false,
                };

                if (metadata && Object.keys(metadata).length > 0) {
                        messagePayload.metadata = metadata;
                }

                const messageRef = await db
                        .collection("Users")
                        .doc(userId)
                        .collection("AIChat")
                        .doc(chatId)
                        .collection("Messages")
                        .add(messagePayload);

                // Update chat document with last message info
                await db
                        .collection("Users")
                        .doc(userId)
                        .collection("AIChat")
                        .doc(chatId)
                        .set({
                                lastMessageTime: firebase.firestore.FieldValue.serverTimestamp(),
                                lastMessage: (() => { const plain = stripMarkdown(content); return plain.substring(0, 100) + (plain.length > 100 ? '...' : ''); })(),
                                updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                        }, { merge: true });

                return messageRef.id;
        } catch (error) {
                console.error("[firestoreUtils][saveMessage] - Error saving message:", error);
                throw error;
        }
};

/**
 * Delete a chat session and all its messages
 * @param {string} userId - User ID
 * @param {string} chatId - Chat session ID to delete
 * @returns {Promise<void>}
 */
export const deleteChat = async (userId, chatId) => {
        try {
                const snapshot = await db
                        .collection("Users")
                        .doc(userId)
                        .collection("AIChat")
                        .doc(chatId)
                        .collection("Messages")
                        .get();

                const batchSize = 450;
                let batch = db.batch();
                let operationCount = 0;

                for (const doc of snapshot.docs) {
                        batch.delete(doc.ref);
                        operationCount++;

                        if (operationCount >= batchSize) {
                                await batch.commit();
                                batch = db.batch();
                                operationCount = 0;
                        }
                }

                if (operationCount > 0) {
                        await batch.commit();
                }

                await db
                        .collection("Users")
                        .doc(userId)
                        .collection("AIChat")
                        .doc(chatId)
                        .delete();
        } catch (error) {
                console.error("[firestoreUtils][deleteChat] - Error deleting chat:", error);
                throw error;
        }
};

/**
 * Create a new chat session
 * @param {string} userId - User ID
 * @returns {Promise<string>} New chat session ID
 */
export const createChatSession = async (userId) => {
        const chatRef = db
                .collection("Users")
                .doc(userId)
                .collection("AIChat")
                .doc();

        const newChatId = chatRef.id;

        await chatRef.set({
                createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                lastMessage: "",
                lastMessageTime: null,
        });

        return newChatId;
};

/**
 * Send a welcome message to a chat session
 * @param {string} userId - User ID
 * @param {string} chatId - Chat session ID
 * @param {string} welcomeText - Welcome message text
 * @returns {Promise<string>} Message document ID
 */
export const sendWelcomeMessage = async (userId, chatId, welcomeText) => {
        return await saveMessage(userId, chatId, welcomeText, "ai");
};

/**
 * Get Firestore database instance
 * @returns {Object} Firestore database instance
 */
export const getDb = () => db;

